// ============================================================
// /aliados · «MI PANEL DE ALIADO» — la versión real (s220)
//
// Se cablea sobre el diseño de /aliados/demo (InfluencerDemo.jsx, mismas clases de influencer.css)
// con el contrato de claude/influencer/CONTRATO_s218_panel_influencer_para_code.md y los cambios de s220
// (error en `error_code`; % / meses / retención salen del código MÁS RECIENTE del aliado).
//
// Regla: solo se dibuja lo que Bubble devuelve. El demo tenía «a cobrar», «en retención», metas, W-9 e
// historias con datos de ejemplo; ningún WF los devuelve todavía, así que aquí no están (una cifra
// inventada en el panel de alguien que cobra comisiones es peor que un hueco).
//
// El aliado es un miembro con un panel extra (s220, Ali): entra por el catálogo y llega aquí desde
// «Mi cuenta». Por eso la cabecera tiene «Volver a NXT» y no un «Salir».
// ============================================================
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { getMyInfluencerPanel, getMyInfluencerPayouts, getUserProfile } from "./api";

const dinero = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
// Las fechas llegan como ms UNIX entre comillas («1791411236587»); vacío = sin fecha.
const fecha = (ms) => { const n = Number(ms); if (!n) return null; const d = new Date(n); return isNaN(d.getTime()) ? null : d; };
const fechaCorta = (d) => d.toLocaleDateString("es-US", { day: "numeric", month: "short" });

// `status` es el display de SubscriptionStatus en minúsculas; vacío = se registró y no tiene membresía.
const ESTADOS = {
  active: { k: "activo", t: "Pagando" },
  trialing: { k: "prueba", t: "En prueba" },
  past_due: { k: "pendiente", t: "Pago pendiente" },
  canceled: { k: "cancelado", t: "Canceló" },
  "": { k: "sin", t: "Sin membresía" },
};
const estadoDe = (s) => ESTADOS[String(s || "").trim().toLowerCase().replace(/\s+/g, "_")] || ESTADOS[""];
// «Ana M.» (decisión de Ali, s218): nombre de pila + inicial. `null` cuenta como vacío.
const nombreDe = (m) => {
  const f = String(m.first ?? "").trim(); const i = String(m.initial ?? "").trim();
  return f ? (i ? `${f} ${i.charAt(0).toUpperCase()}.` : f) : "Sin nombre";
};

function copiarTexto(texto) {
  try {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(texto).then(() => true, () => false);
  } catch (e) { /* sigue abajo */ }
  return Promise.resolve(false);
}
function BotonCopiar({ texto, etiqueta = "Copiar" }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" className="inf-btn inf-btn--linea" onClick={async () => {
      await copiarTexto(texto); setOk(true); setTimeout(() => setOk(false), 1600);
    }}>{ok ? "Copiado" : etiqueta}</button>
  );
}

// ---- Resumen -------------------------------------------------------------------
function Resumen({ p, pagos }) {
  const [vista, setVista] = useState("semana");
  const pct = Number(p.commission_pct) || 0;
  const meses = Number(p.commission_months) || 0;
  const retencion = Number(p.hold_days) || 0;
  const reg = Number(p.registered) || 0;
  const pasos = [
    { k: "Se registraron con tu código", n: reg },
    { k: "Están en su prueba de 7 días", n: Number(p.trialing) || 0 },
    { k: "Pagan su membresía", n: Number(p.active) || 0 },
    { k: "Tienen un pago pendiente", n: Number(p.past_due) || 0 },
    { k: "Cancelaron", n: Number(p.canceled) || 0 },
  ].filter((x, i) => i === 0 || i === 2 || x.n > 0);   // registrados y pagando siempre; el resto, si hay

  // Registros por semana (lunes) o por mes, desde la fecha de alta de cada miembro. Solo lo que hay.
  const serie = useMemo(() => {
    const cubos = new Map();
    p.members.forEach(m => {
      const d = fecha(m.since); if (!d) return;
      let clave, rotulo;
      if (vista === "semana") {
        const l = new Date(d); l.setHours(0, 0, 0, 0); l.setDate(l.getDate() - ((l.getDay() + 6) % 7));
        clave = l.getTime(); rotulo = fechaCorta(l);
      } else {
        clave = d.getFullYear() * 12 + d.getMonth(); rotulo = d.toLocaleDateString("es-US", { month: "long" });
      }
      const c = cubos.get(clave) || { r: rotulo, n: 0 }; c.n++; cubos.set(clave, c);
    });
    return [...cubos.entries()].sort((a, b) => a[0] - b[0]).slice(vista === "semana" ? -10 : -6).map(([, c]) => c);
  }, [p.members, vista]);
  const max = Math.max(1, ...serie.map(s => s.n));

  return (
    <>
      <section className="inf-bloque" aria-labelledby="ali-cond">
        <h2 id="ali-cond" className="inf-h2">Tus condiciones</h2>
        {pct > 0 ? (
          <p className="inf-condiciones">
            <b>{pct} %</b> de cada pago de tus miembros{meses > 0 ? <>, <b>durante {meses} meses</b></> : null}.
            {" "}Se paga el <b>día 15</b> de cada mes, por los cobros que ya tienen{retencion > 0 ? <> <b>{retencion} días</b></> : " los días de retención"} y no se reembolsaron.
          </p>
        ) : (
          <p className="inf-condiciones">Tus condiciones están en tu contrato. Si no las ves aquí, escríbenos.</p>
        )}
      </section>

      <section className="inf-bloque" aria-labelledby="ali-embudo">
        <h2 id="ali-embudo" className="inf-h2">Cómo va tu código</h2>
        <ol className="inf-embudo">
          {pasos.map(x => (
            <li key={x.k} className="inf-embudo__paso">
              <span className="inf-embudo__n">{x.n}</span>
              <span className="inf-embudo__k">{x.k}</span>
              <span className="inf-embudo__barra" aria-hidden="true"><span style={{ width: `${reg ? (x.n / reg) * 100 : 0}%` }} /></span>
            </li>
          ))}
        </ol>
        {Number(p.merchants_referred) > 0 && (
          <p className="inf-nota">Además, {p.merchants_referred} {Number(p.merchants_referred) === 1 ? "comercio entró" : "comercios entraron"} por ti.</p>
        )}
      </section>

      <section className="inf-bloque" aria-labelledby="ali-tend">
        <div className="inf-cabeza">
          <h2 id="ali-tend" className="inf-h2">Registros con tu código</h2>
          <div className="inf-segmento" role="group" aria-label="Agrupar por">
            {[["semana", "Por semana"], ["mes", "Por mes"]].map(([k, t]) => (
              <button key={k} type="button" aria-pressed={vista === k} onClick={() => setVista(k)}>{t}</button>
            ))}
          </div>
        </div>
        {serie.length === 0 ? (
          <p className="inf-nota">Todavía no se registró nadie con tu código. Aquí vas a ver cómo crece.</p>
        ) : (
          <div className={`inf-grafico inf-grafico--${vista}`} role="img" aria-label={serie.map(s => `${s.r}: ${s.n}`).join(", ")}>
            {serie.map((s, i) => (
              <div key={s.r + i} className="inf-grafico__col">
                <span className="inf-grafico__n">{s.n}</span>
                <span className={`inf-grafico__barra${i === serie.length - 1 ? " inf-grafico__barra--ahora" : ""}`} style={{ height: `${(s.n / max) * 100}%` }} />
                <span className="inf-grafico__r">{s.r}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="inf-bloque" aria-labelledby="ali-pagado">
        <h2 id="ali-pagado" className="inf-h2">Lo que ya te pagamos</h2>
        <p className="inf-total">{pagos ? dinero(pagos.total_paid_usd) : "—"}</p>
        <p className="inf-nota">El detalle está en la pestaña Pagos.</p>
      </section>
    </>
  );
}

// ---- Miembros ------------------------------------------------------------------
function Miembros({ p }) {
  const planDe = useMemo(() => {
    const porPrecio = new Map(p.plans.filter(x => x.price_id).map(x => [x.price_id, x.label]));
    return (m) => (m.price_id && porPrecio.get(m.price_id)) || "";
  }, [p.plans]);
  const todos = useMemo(() => p.members
    .map(m => ({ ...m, e: estadoDe(m.status), d: fecha(m.since) }))
    .sort((a, b) => (b.d ? b.d.getTime() : 0) - (a.d ? a.d.getTime() : 0)), [p.members]);
  const [filtro, setFiltro] = useState("todos");
  const [cuantos, setCuantos] = useState(15);
  const filtros = [["todos", "Todos"], ["activo", "Pagando"], ["prueba", "En prueba"], ["pendiente", "Pago pendiente"], ["cancelado", "Cancelaron"], ["sin", "Sin membresía"]];
  const cuenta = (k) => (k === "todos" ? todos.length : todos.filter(m => m.e.k === k).length);
  const lista = filtro === "todos" ? todos : todos.filter(m => m.e.k === filtro);
  return (
    <section className="inf-bloque" aria-labelledby="ali-miembros">
      <h2 id="ali-miembros" className="inf-h2">Tus miembros</h2>
      <p className="inf-nota">Por privacidad ves el nombre de pila, la inicial y el correo a medias.</p>
      {todos.length === 0 ? (
        <p className="inf-nota">Cuando alguien se registre con tu código, aparece aquí.</p>
      ) : (
        <>
          <div className="inf-chips" role="group" aria-label="Filtrar por estado">
            {filtros.filter(([k]) => k === "todos" || cuenta(k) > 0).map(([k, t]) => (
              <button key={k} type="button" className="inf-chip" aria-pressed={filtro === k} onClick={() => { setFiltro(k); setCuantos(15); }}>
                {t} <span className="inf-chip__n">{cuenta(k)}</span>
              </button>
            ))}
          </div>
          <ul className="inf-lista">
            {lista.slice(0, cuantos).map((m, i) => (
              <li key={`${m.since}-${i}`} className="inf-fila inf-fila--miembro">
                <span className="inf-fila__datos">
                  <span className="inf-fila__titulo">{nombreDe(m)}</span>
                  <span className="inf-fila__plan">
                    {[m.email_masked, m.d ? `entró el ${fechaCorta(m.d)}` : "", planDe(m)].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className={`inf-estado inf-estado--${m.e.k}`}>{m.e.t}</span>
              </li>
            ))}
          </ul>
          {lista.length > cuantos && (
            <button type="button" className="inf-btn inf-btn--linea inf-btn--ancho" onClick={() => setCuantos(c => c + 15)}>
              Ver más ({lista.length - cuantos})
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ---- Pagos ---------------------------------------------------------------------
function Pagos({ pagos }) {
  if (!pagos) return <section className="inf-bloque"><p className="inf-nota">No pudimos cargar tus pagos ahora. Vuelve a intentar en un momento.</p></section>;
  return (
    <section className="inf-bloque" aria-labelledby="ali-pagos">
      <h2 id="ali-pagos" className="inf-h2">Tus pagos</h2>
      {pagos.payouts.length === 0 ? (
        <p className="inf-nota">Todavía no hay pagos. Las comisiones se pagan el día 15 de cada mes.</p>
      ) : (
        <ul className="inf-lista">
          {pagos.payouts.map((x, i) => {
            const d = fecha(x.paid_at);
            return (
              <li key={`${x.period}-${i}`} className="inf-fila inf-fila--pago">
                <span className="inf-fila__datos">
                  <span className="inf-fila__titulo">{x.period || "Pago"}</span>
                  <span className="inf-fila__plan">{[x.method, d ? `pagado el ${fechaCorta(d)}` : ""].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="inf-fila__monto">{dinero(x.amount_usd)}</span>
                <span className="inf-estado inf-estado--activo">Pagado</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ---- Para vender ---------------------------------------------------------------
function ParaVender({ codigos }) {
  const codigo = codigos[codigos.length - 1] || "";   // `codes` viene en orden de alta: el último es el vigente
  const link = `${window.location.origin}/i/${encodeURIComponent(codigo)}`;
  const [qr, setQr] = useState("");
  useEffect(() => {
    let vivo = true;
    if (codigo) QRCode.toDataURL(link, { margin: 2, width: 560, errorCorrectionLevel: "M", color: { dark: "#0F1B2A", light: "#FFFFFF" } })
      .then(u => { if (vivo) setQr(u); }).catch(() => {});
    return () => { vivo = false; };
  }, [codigo, link]);
  const textos = [
    `Encontré la forma de salir en Miami gastando menos: con NXT Reserve tienes beneficios en restaurantes, spas y rooftops. Entra con mi código ${codigo}. #publicidad`,
    `¿Plan de viernes? Yo ya uso NXT Reserve: llegas, muestras tu código y ahorras en la cuenta. Entra con ${codigo}. #publicidad`,
  ];
  return (
    <>
      <section className="inf-bloque" aria-labelledby="ali-link">
        <h2 id="ali-link" className="inf-h2">Tu link y tu código</h2>
        <div className="inf-campo">
          <span className="inf-campo__valor">{link.replace(/^https?:\/\//, "")}</span>
          <BotonCopiar texto={link} />
        </div>
        <div className="inf-campo">
          <span className="inf-campo__valor inf-campo__valor--codigo">{codigo}</span>
          <BotonCopiar texto={codigo} />
        </div>
        {codigos.length > 1 && <p className="inf-nota">Tus otros códigos siguen valiendo: {codigos.slice(0, -1).join(", ")}.</p>}
        <div className="inf-qr">
          {qr ? <img src={qr} alt={`Código QR de ${link}`} width="168" height="168" /> : <span className="inf-qr__vacio" />}
          <div>
            <p className="inf-qr__texto">Ponlo en tus historias o imprímelo: lleva directo a tu link.</p>
            <a className={`inf-btn inf-btn--linea${qr ? "" : " inf-btn--apagado"}`} href={qr || undefined} download={`QR-${codigo}.png`}
              aria-disabled={!qr} onClick={e => { if (!qr) e.preventDefault(); }}>Descargar QR</a>
          </div>
        </div>
      </section>
      <section className="inf-bloque" aria-labelledby="ali-textos">
        <h2 id="ali-textos" className="inf-h2">Textos sugeridos</h2>
        <p className="inf-nota">Ya traen <b>#publicidad</b>: la ley de EE. UU. (FTC) pide decir que es contenido pagado.</p>
        {textos.map((t, i) => (
          <div key={i} className="inf-texto"><p>{t}</p><BotonCopiar texto={t} etiqueta="Copiar texto" /></div>
        ))}
      </section>
    </>
  );
}

// ---- La página -------------------------------------------------------------------
const PESTANAS = [["resumen", "Resumen"], ["miembros", "Miembros"], ["pagos", "Pagos"], ["vender", "Para vender"]];

export default function AliadoPanel() {
  const navigate = useNavigate();
  const [estado, setEstado] = useState("cargando");   // cargando | ok | sin_codigo | error
  const [panel, setPanel] = useState(null);
  const [pagos, setPagos] = useState(null);
  const [nombre, setNombre] = useState("");
  const [pestana, setPestana] = useState("resumen");
  useEffect(() => { document.title = "Mi panel de aliado · NXT Reserve"; }, []);

  useEffect(() => {
    let vivo = true;
    getUserProfile().then(u => { if (vivo) setNombre(String(u?.name || "").trim().split(/\s+/)[0] || ""); }).catch(() => {});
    getMyInfluencerPanel()
      .then(p => { if (vivo) { setPanel(p); setEstado("ok"); } })
      .catch(e => { if (vivo) setEstado(e?.code === "NO_CODE" ? "sin_codigo" : "error"); });
    // Los pagos van aparte: si fallan, el resto del panel se ve igual.
    getMyInfluencerPayouts().then(x => { if (vivo) setPagos(x); }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  const codigos = panel ? String(panel.codes || "").split(",").map(s => s.trim()).filter(Boolean) : [];
  const ir = (k) => { setPestana(k); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="inf inf--real">
      <header className="inf-cabecera">
        <div className="inf-cabecera__fila">
          <button type="button" className="inf-cabecera__volver" onClick={() => navigate("/")}>← NXT RESERVE</button>
          {codigos.length > 0 && <span className="inf-cabecera__codigo">{codigos[codigos.length - 1]}</span>}
        </div>
        <p className="inf-cabecera__hola">{nombre ? `Hola, ${nombre}. ` : ""}Miembros pagando con tu código</p>
        <p className="inf-cabecera__cifra">{estado === "ok" ? Number(panel.active) || 0 : "—"}</p>
        <p className="inf-cabecera__fecha">Las comisiones se pagan el día 15 de cada mes.</p>
      </header>

      {estado === "ok" && (
        <nav className="inf-pestanas" aria-label="Secciones del panel">
          {PESTANAS.map(([k, t]) => (
            <button key={k} type="button" aria-current={pestana === k ? "page" : undefined} onClick={() => ir(k)}>{t}</button>
          ))}
        </nav>
      )}

      <main className="inf__columna">
        {estado === "cargando" && <section className="inf-bloque"><p className="inf-nota">Cargando tu panel…</p></section>}
        {estado === "sin_codigo" && (
          <section className="inf-bloque">
            <h2 className="inf-h2">Todavía no tienes un código asignado</h2>
            <p className="inf-nota">Cuando el equipo de NXT te asigne tu código, aquí vas a ver tus miembros y tus comisiones.</p>
          </section>
        )}
        {estado === "error" && (
          <section className="inf-bloque"><p className="inf-nota">No pudimos cargar tu panel ahora. Vuelve a intentar en un momento.</p></section>
        )}
        {estado === "ok" && (
          pestana === "resumen" ? <Resumen p={panel} pagos={pagos} />
            : pestana === "miembros" ? <Miembros p={panel} />
            : pestana === "pagos" ? <Pagos pagos={pagos} />
            : <ParaVender codigos={codigos} />
        )}
      </main>
    </div>
  );
}
